'use client'

import { Button } from '@/ui/shadcn/button';
import { Loader2, X } from 'lucide-react';
import React, { FormEvent, useState } from 'react';
import { Label } from '@/ui/shadcn/label';
import { Input } from '@/ui/shadcn/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/ui/shadcn/input-otp';
import { phoneNumber } from '@/lib/auth-client';

const LoginWhatsappModal = ({ closeAction }: { closeAction: () => void }) => {

    const [isPending, setIsPending] = useState(false)
    const [isWaitingForOtp, setIsWaitingForOtp] = useState(false)
	const [error, setError] = useState<string | null>(null)
    const [phone, setPhone] = useState<string>("")
    const [otp, setOtp] = useState<string>("")

    const normalizePhoneNumber = (phoneNumber: string): string => {
        const cleaned = phoneNumber.trim().replace(/^\+/, ''); // Remove leading +
        if (cleaned.startsWith('221')) {
            return cleaned;
        }
        return `221${cleaned}`;
    }

    const getPhoneNumberWithoutCountryCode = (phoneNumber: string): string => {
        const cleaned = phoneNumber.trim().replace(/^\+/, ''); // Remove leading +
        return cleaned.startsWith('221') ? cleaned.slice(3) : cleaned;
    }

    const isValidPhonePrefix = (phoneNumber: string): boolean => {
        const numberWithoutCountryCode = getPhoneNumberWithoutCountryCode(phoneNumber);
        const validPrefixes = ['70', '72', '75', '76', '77', '78'];
        return validPrefixes.some(prefix => numberWithoutCountryCode.startsWith(prefix));
    }

    const isValidPhoneLength = (phoneNumber: string): boolean => {
        const numberWithoutCountryCode = getPhoneNumberWithoutCountryCode(phoneNumber);
        // Remove any non-digit characters and check length
        const digitsOnly = numberWithoutCountryCode.replace(/\D/g, '');
        return digitsOnly.length === 9;
    }

    const sendOtpAction = async (e?: FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        
        setError(null)
        
        // Validate phone number before proceeding
        if (!phone || phone.trim() === '') {
            setError("Veuillez renseigner votre numéro de téléphone");
            return;
        }

        // Validate phone number length
        if (!isValidPhoneLength(phone)) {
            setError("Le numéro de téléphone doit contenir exactement 9 chiffres");
            return;
        }

        // Validate phone number prefix
        if (!isValidPhonePrefix(phone)) {
            setError("Le numéro de téléphone doit commencer par 70, 72, 75, 76, 77 ou 78");
            return;
        }

        const normalizedPhone = normalizePhoneNumber(phone);

        setIsPending(true)
        await phoneNumber.sendOtp(
            {
                phoneNumber: normalizedPhone,
                completeUser: false
            } as any,
            {
                onResponse: (ctx) => {
                    setIsPending(false);
                },
                onError: (ctx) => {
                    setError(ctx.error.message || "An error occurred");
                },
                onSuccess: (ctx) => {
                    setIsWaitingForOtp(true)
                }
            }
        );
    }

    const verifyOtpAction = async () => {
        setError(null)
        setIsPending(true)
        
        const normalizedPhone = normalizePhoneNumber(phone);
        
        await phoneNumber.verify(
            {
                phoneNumber: normalizedPhone,
                code: otp,
            },
            {
                onResponse: (ctx) => {
                    setIsPending(false);
                },
                onError: (ctx) => {
                    setError(ctx.error.message || "An error occurred");
                },
                onSuccess: (ctx) => {
                    closeAction()
                    window.location.href = "/account/profile";
                    setIsPending(false);
                }
            }
        );
    }

    return (
        <div className="login-modal flex flex-col items-center">
            <button className="absolute top-6 right-6 cursor-pointer" onClick={closeAction}>
                <X className="size-6" />
            </button>
            <h2 className="text-3xl font-alt text-center font-regular mb-4 text-dark">
                Se connecter par SMS
            </h2>
            <p className="w-full text-center">
                Renseignez votre numéro de téléphone pour continuer
            </p>
            <hr className="w-9/12 h-[1px] border-black my-8 px-4" />
            <form className="w-full">
                <div className="w-full space-y-2 mb-2">
                    <Label htmlFor="phone">Votre numéro de téléphone :</Label>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">+221</span>
                        <Input 
                            id="phone" name="phone" 
                            type="text" placeholder="Exemple : 770975523" 
                            onChange={(e) => setPhone(e.target.value)} value={phone}
                            disabled={isPending || isWaitingForOtp}
                            className="flex-1"
                        />
                    </div>
                </div>
                {
                    isWaitingForOtp ? (
                        <>
                            <div className="w-full space-y-2 mb-2 mt-4">
                                <Label htmlFor="otp">Code de vérification :</Label>
                                <InputOTP maxLength={6} className="mx-auto" value={otp} onChange={(value) => setOtp(value)}>
                                    <InputOTPGroup>
                                        <InputOTPSlot index={0} />
                                        <InputOTPSlot index={1} />
                                        <InputOTPSlot index={2} />
                                        <InputOTPSlot index={3} />
                                        <InputOTPSlot index={4} />
                                        <InputOTPSlot index={5} />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>
                            <Button
                                formAction={verifyOtpAction}
                                size="xl"
                                className="w-full mb-3 mt-6"
                                disabled={isPending}
                            >
                                { isPending && <Loader2 className="animate-spin" /> }
                                Envoyer le code de vérification
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="button"
                            onClick={(e) => sendOtpAction(e)}
                            size="xl"
                            className="w-full mb-3 mt-6"
                            disabled={isPending || !phone.trim()}
                        >
                            { isPending && <Loader2 className="animate-spin" /> }
                            Envoyer le code de vérification
                        </Button>
                    )
                }

            </form>
            {error && (
                <div className="w-full mt-4 text-red-600 text-center">
                    {error}
                </div>
            )}
        </div>
    );
};

export default LoginWhatsappModal;
